/* eslint-disable react-hooks/rules-of-hooks */
import {
  BaseQueryFn,
  createApi,
  fetchBaseQuery,
  retry,
} from "@reduxjs/toolkit/query/react";

import { Mutex } from "async-mutex";
import { toast } from "components/ui/use-toast";
import { Tags } from "@/enum/tags";
import { shouldNotRetry } from "./no-retry-routes";
import { featureFlags } from "@/config/feature-flag";
import { excludedRoutes } from "@/routes/public-route-map";
import { withRhMocks } from "./mocks/rh-mock-api";

// Toast throttle para evitar toasts duplicados/excessivos
const toastThrottle = {
  lastShown: {} as Record<string, number>,
  cooldown: 5000, // 5 segundos entre toasts do mesmo tipo

  canShow(key: string): boolean {
    const now = Date.now();
    const lastTime = this.lastShown[key] || 0;
    if (now - lastTime > this.cooldown) {
      this.lastShown[key] = now;
      return true;
    }
    return false;
  }
};

// Função para verificar se a rota atual está excluída
const isExcludedRoute = (): boolean => {
  if (typeof window === 'undefined') return false;
  return excludedRoutes.some(route => window.location.pathname.startsWith(route));
};

// Função para extrair token da URL
const getTokenFromUrl = (): string | null => {
  if (typeof window === 'undefined') return null;
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('at');
};
  
// Cache para IP com invalidação automática
let cachedIP: string | null = null;
let ipCacheTimestamp: number = 0;
const IP_CACHE_DURATION = 15 * 60 * 1000;

// Singleton promise para evitar chamadas concorrentes ao dar F5
let fetchIPPromise: Promise<string | null> | null = null;

const fetchIP = async (): Promise<string | null> => {
  const now = Date.now();

  // Retorna cache se ainda válido
  if (cachedIP && now - ipCacheTimestamp < IP_CACHE_DURATION) {
    return cachedIP;
  }

  // Se já existe uma chamada em andamento, reutiliza a mesma promise
  if (fetchIPPromise) {
    return fetchIPPromise;
  }

  fetchIPPromise = (async () => {
    // Priorizar bigdatacloud que está mais estável
    const apis = [
      "https://api.bigdatacloud.net/data/client-ip",
      "https://api.ipify.org/?format=json",
    ];

    for (const api of apis) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000); // 3s timeout

        const response = await fetch(api, { signal: controller.signal });
        clearTimeout(timeoutId);

        if (!response.ok) {
          continue;
        }
        const data = await response.json();
        const ip = data.ip || data.ipString || data.clientIp;

        if (ip) {
          cachedIP = ip;
          ipCacheTimestamp = Date.now();
          return ip;
        }
      } catch {
        // Silently fail - IP is not critical
        continue;
      }
    }

    // Retorna cache antigo se existir, senão placeholder
    return cachedIP || "0.0.0.0";
  })();

  try {
    return await fetchIPPromise;
  } finally {
    fetchIPPromise = null;
  }
};

// invalidar cache do IP (chamada quando token expira)
const invalidateIPCache = () => {
  cachedIP = null;
  ipCacheTimestamp = 0;
};

export const loginSlice = createApi({
  reducerPath: "api-login",
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_REACT_API_URL,
    prepareHeaders: async (headers) => {
      const ip = await fetchIP();
      if (ip) {
        headers.set("x-forwarded-for", ip);
      }
      return headers;
    },
  }),
  endpoints: () => ({}),
});

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_REACT_API_URL,
  prepareHeaders: async (headers) => {
    const ip = await fetchIP();

    // Portal pt-BR: garante mensagens do backend em português (getLanguage no back lê este header).
    headers.set("accept-language", "pt-BR");

    // Para rotas públicas, verifica se há token na URL ou localStorage
    if (isExcludedRoute()) {
      const urlToken = getTokenFromUrl();
      const localStorageToken = localStorage.getItem("@azure:token");
      
      // Prioriza token da URL, mas usa localStorage se URL não tiver token
      const tokenToUse = urlToken || localStorageToken;
      
      if (tokenToUse) {
        headers.set("Authorization", `Bearer ${tokenToUse}`);
        console.log(`[HTTP] Using token for public route: ${urlToken ? 'from URL' : 'from localStorage'}`);
      }
    }

    if (featureFlags.USE_KEYCLOAK) {
      const kcToken = localStorage.getItem("@azure:kc_token");
      const internalToken = localStorage.getItem("@azure:token");

      if (kcToken) headers.set("Authorization", `Bearer ${kcToken}`);
      if (internalToken) headers.set("x-internal-token", internalToken);
      if (ip) headers.set("x-forwarded-for", ip);
    } else {
      const token = localStorage.getItem("@azure:token");
      if (token) {
        if (ip) headers.set("x-forwarded-for", ip);
        headers.set("Authorization", `Bearer ${token}`);
      }
    }
    return headers;
  },
});

const mutex = new Mutex();

type ReconcileDispatch = (action: { type: string }) => unknown;

export async function refreshAccessToken(
  dispatch?: ReconcileDispatch,
): Promise<boolean> {
  const handleFailure = (): void => {
    dispatch?.({ type: "auth/logout" });
    invalidateIPCache();
    if (!isExcludedRoute()) {
      window.location.href = "/login";
    }
  };

  if (mutex.isLocked()) {
    await mutex.waitForUnlock();
    return Boolean(localStorage.getItem("@azure:token"));
  }

  const release = await mutex.acquire();
  try {
    const refreshToken = localStorage.getItem("@azure:refresh_token");

    if (!refreshToken) {
      handleFailure();
      return false;
    }

    const ip = await fetchIP();

    const refreshHeaders: Record<string, string> = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${refreshToken}`,
    };

    if (ip) {
      refreshHeaders["x-forwarded-for"] = ip;
    }

    const refreshResponse = await fetch(
      `${import.meta.env.VITE_REACT_API_URL}/auth/refresh`,
      { method: "GET", headers: refreshHeaders },
    );

    if (!refreshResponse.ok) {
      handleFailure();
      return false;
    }

    const refreshData = await refreshResponse.json();
    localStorage.setItem("@azure:token", refreshData.access_token);
    localStorage.setItem("@azure:refresh_token", refreshData.refresh_token);
    return true;
  } catch {
    handleFailure();
    return false;
  } finally {
    release();
  }
}

const baseQueryWithReauth: BaseQueryFn = async (args, api, extraOptions) => {
  await mutex.waitForUnlock();

  let result = await baseQuery(args, api, extraOptions);
  if (args.url === "/auth/me" && result.error && result.error.status === 500) {
    localStorage.clear();
    invalidateIPCache();
    if (!isExcludedRoute()) {
      window.location.href = "/login";
    }
  }

  const operatorValidate = localStorage.getItem("@verzel:operator-access");

  if (
    args.url === "/public/auth/operator/me" &&
    result.error &&
    result.error.status === 500 &&
    operatorValidate === "true"
  ) {
    localStorage.clear();
    invalidateIPCache();
  }

  if (result.error && result.error.status === 423) {
    // Usar throttle para evitar toasts duplicados
    if (toastThrottle.canShow("error-423")) {
      toast({
        title: "Erro",
        description: "Usuário bloqueado! Você está sendo deslogado do sistema.",
        variant: "destructive",
      });
    }

    setTimeout(() => {
      localStorage.clear();
      invalidateIPCache();
      api.dispatch({ type: "auth/logout" });
      if (!isExcludedRoute()) {
        window.location.href = "/login";
      }
    }, 2000);

    return result;
  }

  // Em rotas públicas, ignorar erros 403 de endpoints que não deveriam ser chamados
  if (result.error && result.error.status === 403) {
    // Não mostrar toast em rotas públicas (notificações, etc não devem ser chamados lá)
    if (isExcludedRoute()) {
      return result;
    }

    if (
      !result.error?.data ||
      (result.error?.data as any)?.message === "Forbidden resource" ||
      ((result.error?.data as any)?.messages &&
        Array.isArray((result.error?.data as any).messages) &&
        (result.error?.data as any).messages.some((msg: string) =>
          msg.includes("não autorizado")
        ))
    ) {
      // Usar throttle para evitar toasts duplicados
      if (toastThrottle.canShow("error-403")) {
        toast({
          title: "Informação!",
          description: "Você não tem permissão para acessar este recurso.",
          variant: "destructive",
        });
      }
    }
  }

  if (result.error && result.error.status === 401) {
    const refreshed = await refreshAccessToken(api.dispatch);
    if (refreshed) {
      result = await baseQuery(args, api, extraOptions);
    }
  }

  return result;
};

const baseQueryWithCustomRetry: BaseQueryFn = async (
  args,
  api,
  extraOptions
) => {
  await mutex.waitForUnlock();

  let maxRetries = 1;
  const method = typeof args === "string" ? "GET" : args.method || "GET";
  const url = typeof args === "string" ? args : args.url;

  if (
    shouldNotRetry(url) ||
    ["POST", "PATCH", "DELETE", "PUT"].includes(method.toUpperCase())
  ) {
    maxRetries = 0;
  }

  const runQuery = (args: any, api: any, extraOptions: any) =>
    baseQueryWithReauth(args, api, extraOptions);

  if (maxRetries > 0) {
    return retry(runQuery, { maxRetries })(args, api, extraOptions);
  }

  return runQuery(args, api, extraOptions);
};

export const apiSlice = createApi({
  reducerPath: "api",
  tagTypes: Object.values(Tags),
  baseQuery: withRhMocks(baseQueryWithCustomRetry),
  endpoints: () => ({}),
  keepUnusedDataFor: 300,
  refetchOnMountOrArgChange: false,
});
